import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Flex } from "@mantine/core";
import { IconDownload, IconLink, IconLock, IconLockOpen } from "@tabler/icons-react";

import { Button, PublicLinkModal, Text } from "components";
import { AuthorityConstants, ServiceConstants } from "constant";
import { CreditService } from "services";
import { displayClasses } from "styles";
import { CreditResponse, CreditStatus } from "types"
import { AppUtils, UserUtils } from "utils";
import { IRootState } from "config/redux";

import ActiveStatusActions from "./status/active";
import ApprovedStatusActions from "./status/approved";
import DraftStatusActions from "./status/draft";
import ProcessingStatusActions from "./status/processing";
import SentToBankStatusActions from "./status/sent_to_bank";
import SignedIncompleteStatusActions from "./status/signed_incomplete";
import SigningStatusActions from "./status/signing";
import UnRequestedStatusActions from "./status/unrequested";

const { ROLE_CC_U, ROLE_CR_C } = AuthorityConstants;
const { REQUEST_CREDIT_STATUS_CHANGE_ID, REQUEST_CREDIT_REQUEST_ID, REQUEST_CREDIT_DOWNLOAD_ID, REQUEST_PENDING, REQUEST_CREDIT_LINK_CREATE_ID, REQUEST_CREDIT_LOCK_ID, REQUEST_CREDIT_UNLOCK_ID } = ServiceConstants;

interface CreditHeaderProps {
  credit: CreditResponse;
}

const CreditHeader = ({ credit }: CreditHeaderProps) => {
  const { t } = useTranslation();
  const downloadServiceObject = useSelector((state: IRootState) => state.service.object[REQUEST_CREDIT_DOWNLOAD_ID]);
  const lockServiceObject = useSelector((state: IRootState) => state.service.object[REQUEST_CREDIT_LOCK_ID]);
  const unlockServiceObject = useSelector((state: IRootState) => state.service.object[REQUEST_CREDIT_UNLOCK_ID]);
  const [linkVisible, setLinkVisible] = useState<boolean>(false);
  const [locked, setLocked] = useState<boolean>(credit.locked);

  useEffect(() => {
    AppUtils.resolveServiceObject(downloadServiceObject, REQUEST_CREDIT_DOWNLOAD_ID, async (val) => {
      const url = window.URL.createObjectURL(new Blob([val]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${credit.user.fullName}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    });
    AppUtils.resolveServiceObject(lockServiceObject, REQUEST_CREDIT_LOCK_ID, () => setLocked(true));
    AppUtils.resolveServiceObject(unlockServiceObject, REQUEST_CREDIT_UNLOCK_ID, () => setLocked(false));
  }, [downloadServiceObject, lockServiceObject, unlockServiceObject])

  const changeStatus = useCallback(async (status: CreditStatus | 'REQUEST') => {
    if (status === 'REQUEST') CreditService.creditRequestRequest(credit.id)
    else CreditService.creditStatusChange(credit.id, status as CreditStatus);
  }, [credit]);

  const downloadAsZip = useCallback(() => CreditService.creditDownload(credit.id), [credit]);
  const lockCredit = useCallback(() => CreditService.creditLock(credit.id), [credit]);
  const unlockCredit = useCallback(() => CreditService.creditUnlock(credit.id), [credit]);

  return (
    <Flex justify={'space-between'} align={'center'} className={displayClasses.header}>
      <Flex>
        <Text size={'lg'} mr={'xs'}>{t('credit.current')}</Text>
        <Button onClick={downloadAsZip} size={'compact-xs'} variant={'transparent'}><IconDownload color={'var(--mantine-color-gray-7)'} /></Button>
        <Button size={'compact-xs'} variant={'transparent'} onClick={() => setLinkVisible(true)}><IconLink color={'var(--mantine-color-gray-7)'} /></Button>
        <PublicLinkModal
          opened={linkVisible}
          onClose={() => setLinkVisible(false)}
          create={() => CreditService.creditLinkCreate(credit.id)}
          requestId={REQUEST_CREDIT_LINK_CREATE_ID}
          t={t}
          link={credit.link}
          authority={ROLE_CR_C}
          object={'credit'}
        />
        {locked ?
          <Button onClick={unlockCredit} size={'compact-xs'} variant={'transparent'}><IconLockOpen size={23} color={'var(--mantine-color-gray-7)'} /></Button>
        : <Button onClick={lockCredit} size={'compact-xs'} variant={'transparent'}><IconLock size={23} color={'var(--mantine-color-gray-7)'} /></Button>}
      </Flex>
      <Flex align={'center'}>
        <Text>{t('credit.status.label') + ':'}</Text>
        <CreditStatusLabel status={credit.status} />
        <CreditStatusButtons status={credit.status} requestedAt={credit.requestedAt} onClick={changeStatus} />
      </Flex>
    </Flex>
  )
}

interface CreditStatusLabelProps {
  status: CreditResponse['status'];
}

const CreditStatusLabel = ({ status }: CreditStatusLabelProps) => {
  const { t } = useTranslation();

  if (['REJECTED', 'CANCELED', 'CANCELED_BANK', 'CANCELED_CLIENT'].includes(status)) {
    return <Text ml={'xs'} c={'var(--mantine-color-error)'} >{t('credit.status.name.' + status)}</Text>
  }

  if (['SETTLED'].includes(status)) {
    return <Text ml={'xs'} c={'var(--mantine-primary-color-6)'} >{t('credit.status.name.' + status)}</Text>
  }

  return <Text ml={'xs'}>{t('credit.status.name.' + status)}</Text>
}

interface CreditStatusButtonsProps extends CreditStatusLabelProps {
  requestedAt: CreditResponse['requestedAt'];
  onClick: (val: CreditStatus | 'REQUEST') => void;
}

const CreditStatusButtons = ({ status, requestedAt, onClick }: CreditStatusButtonsProps) => {
  const creditStatusServiceObject = useSelector((state: IRootState) => state.service.object[REQUEST_CREDIT_STATUS_CHANGE_ID]);
  const creditRequestServiceObject= useSelector((state: IRootState) => state.service.object[REQUEST_CREDIT_REQUEST_ID]);

  const isLoading = useMemo(() =>
    (creditRequestServiceObject && creditRequestServiceObject.status === REQUEST_PENDING)
    || (creditStatusServiceObject && creditStatusServiceObject.status === REQUEST_PENDING)
    , [creditRequestServiceObject, creditStatusServiceObject]);

  if (!UserUtils.hasAuthority(ROLE_CC_U)) {
    return <></>;
  }

  if (!requestedAt) {
    return <UnRequestedStatusActions onClick={onClick} isLoading={isLoading} />
  }

  if (status === 'DRAFT') {
    return <DraftStatusActions onClick={onClick} isLoading={isLoading} />
  }

  if (status === 'PROCESSING') {
    return <ProcessingStatusActions onClick={onClick} isLoading={isLoading} />
  }

  if (status === 'APPROVED') {
    return <ApprovedStatusActions onClick={onClick} isLoading={isLoading} />
  }

  if (status === 'SIGNING') {
    return <SigningStatusActions onClick={onClick} isLoading={isLoading} />
  }

  if (status === 'SIGNED_INCOMPLETE') {
    return <SignedIncompleteStatusActions onClick={onClick} isLoading={isLoading} />
  }

  if (status === 'SENT_TO_BANK') {
    return <SentToBankStatusActions onClick={onClick} isLoading={isLoading} />
  }

  if (status === 'ACTIVE') {
    return <ActiveStatusActions onClick={onClick} isLoading={isLoading} />
  }

  return <></>;
}

export default CreditHeader;